import React, { useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Button,
  Menu,
  MenuItem,
  Paper,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ChevronUp,
  ChevronDown,
  Copy,
  Check,
  Search as SearchIcon,
  Globe,
  Clock,
  Image,
} from 'lucide-mui';
import { useTags } from '../../../../hooks/useTags';
import { CHAPTERS_EXPANDED_KEY } from '../../../../constants/tags';
import { LanguageCode } from '../../translate';
import LanguageDialog from '../../translate/LanguageDialog';

interface ChapterToolbarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  isAllExpanded: boolean;
  onExpandAll: () => void;
  onCopyChapters: () => void;
  copySuccess: boolean;
  showTimestamps: boolean;
  showScreenshots: boolean;
  onToggleTimestamps: () => void;
  onToggleScreenshots: () => void;
  isTranslating: boolean;
  onTranslate: (targetLang: LanguageCode) => void;
  isAudioOnly: boolean;
}

const ChapterToolbar: React.FC<ChapterToolbarProps> = ({
  searchTerm,
  onSearchChange,
  isAllExpanded,
  onExpandAll,
  onCopyChapters,
  copySuccess,
  showTimestamps,
  showScreenshots,
  onToggleTimestamps,
  onToggleScreenshots,
  isTranslating,
  onTranslate,
  isAudioOnly,
}) => {
  const { setTag } = useTags();
  const [translateDialogOpen, setTranslateDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExpandAll = () => {
    onExpandAll();
    setTag({
      key: CHAPTERS_EXPANDED_KEY,
      value: (!isAllExpanded).toString(),
    });
    handleClose();
  };

  const handleTranslateClick = () => {
    setTranslateDialogOpen(true);
    handleClose();
  };

  const handleTranslateClose = () => {
    setTranslateDialogOpen(false);
  };

  const handleLanguageSelect = (language: LanguageCode) => {
    onTranslate(language);
    handleTranslateClose();
  };

  const handleCopyClick = () => {
    onCopyChapters();
    handleClose();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
        marginBottom: 1,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexGrow: 1,
          backgroundColor: 'background.default',
          borderRadius: '20px',
          height: '40px',
          border: 'none',
          '& .MuiOutlinedInput-root': {
            height: '100%',
            borderRadius: '20px',
            '& fieldset': {
              borderColor: 'transparent',
            },
            '&:hover fieldset': {
              borderColor: 'transparent',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'transparent',
            },
            '& .MuiInputAdornment-root': {
              '& .MuiSvgIcon-root': {
                fontSize: '20px',
                color: 'text.secondary',
                transition: 'color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
              },
            },
            '&:hover .MuiInputAdornment-root .MuiSvgIcon-root': {
              color: 'text.primary',
            },
          },
        }}
      >
        <TextField
          placeholder="Search Summary..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          variant="outlined"
          fullWidth
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>
      <Button
        variant="text"
        onClick={handleClick}
        endIcon={<ChevronDown />}
        sx={{
          height: '40px',
          textTransform: 'none',
          color: 'text.primary',
          borderRadius: '20px',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {isMobile ? 'Options' : 'Summary\u00A0Options'}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleTranslateClick} disabled={isTranslating}>
          <ListItemIcon>
            <Globe fontSize="small" />
          </ListItemIcon>
          <ListItemText>Translate</ListItemText>
        </MenuItem>
        <MenuItem onClick={onToggleTimestamps}>
          <ListItemIcon>
            <Clock
              fontSize="small"
              color={showTimestamps ? 'primary' : 'inherit'}
            />
          </ListItemIcon>
          <ListItemText>Show Timestamps</ListItemText>
        </MenuItem>
        {!isAudioOnly && (
          <MenuItem onClick={onToggleScreenshots}>
            <ListItemIcon>
              <Image
                fontSize="small"
                color={showScreenshots ? 'primary' : 'inherit'}
              />
            </ListItemIcon>
            <ListItemText>Show Screenshots</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleExpandAll}>
          <ListItemIcon>
            {isAllExpanded ? (
              <ChevronUp fontSize="small" color="primary" />
            ) : (
              <ChevronDown fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {isAllExpanded ? 'Collapse All' : 'Expand All'}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopyClick}>
          <ListItemIcon>
            {copySuccess ? (
              <Check fontSize="small" color="success" />
            ) : (
              <Copy fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {copySuccess ? 'Copied!' : 'Copy Summary'}
          </ListItemText>
        </MenuItem>
      </Menu>

      <LanguageDialog
        open={translateDialogOpen}
        onClose={handleTranslateClose}
        onSelect={handleLanguageSelect}
        isTranslating={isTranslating}
        title="Translate Summary"
      />
    </Box>
  );
};

export default ChapterToolbar;
